import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Users, Plus, Pencil, Trash2, Shield, ShieldCheck, Eye, Save, UserCheck, Link2, RefreshCw, KeyRound } from 'lucide-react';

const ROLES = [
  { id: 'admin', label: 'Administrateur', desc: 'Acces complet a tous les modules', color: 'text-red-600' },
  { id: 'manager', label: 'Manager', desc: 'Acces aux modules autorisés', color: 'text-blue-600' },
  { id: 'employe', label: 'Employé', desc: 'Acces limité aux modules autorisés', color: 'text-green-600' },
  { id: 'lecture', label: 'Lecture seule', desc: 'Consultation uniquement', color: 'text-gray-600' },
];

function UserFormDialog({ modules, existingUser, onSave, trigger, employes = [] }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    email: '', nom: '', role: 'manager', password: '', permissions: [], employe_id: ''
  });

  useEffect(() => {
    if (open && existingUser) {
      setForm({
        email: existingUser.email,
        nom: existingUser.nom,
        role: existingUser.role,
        password: '',
        permissions: existingUser.permissions || [],
        employe_id: existingUser.employe_id || '',
      });
    } else if (open) {
      setForm({ email: '', nom: '', role: 'manager', password: '', permissions: [], employe_id: '' });
    }
  }, [open, existingUser]);

  const isAdmin = form.role === 'admin';

  const togglePermission = (modId) => {
    setForm(f => ({
      ...f,
      permissions: f.permissions.includes(modId)
        ? f.permissions.filter(p => p !== modId)
        : [...f.permissions, modId]
    }));
  };

  const toggleAll = () => {
    const allIds = modules.map(m => m.id);
    setForm(f => ({
      ...f,
      permissions: f.permissions.length === allIds.length ? [] : allIds
    }));
  };

  const handleSubmit = () => {
    if (!form.email || !form.nom) {
      toast.error('Email et nom requis');
      return;
    }
    if (!existingUser && !form.password) {
      toast.error('Mot de passe requis pour un nouvel utilisateur');
      return;
    }
    const payload = { ...form };
    if (existingUser) payload.employe_id = form.employe_id || '';
    onSave(payload, existingUser?.id);
    setOpen(false);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{existingUser ? 'Modifier l\'utilisateur' : 'Nouvel utilisateur'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Nom</Label>
              <Input value={form.nom} onChange={e => setForm(f => ({ ...f, nom: e.target.value }))} data-testid="user-nom-input" />
            </div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                disabled={!!existingUser} data-testid="user-email-input" />
            </div>
          </div>

          <div>
            <Label>Role</Label>
            <Select value={form.role} onValueChange={v => setForm(f => ({ ...f, role: v }))}>
              <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES.map(r => (
                  <SelectItem key={r.id} value={r.id}>
                    <span className={r.color}>{r.label}</span> — {r.desc}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label className="flex items-center gap-1.5"><Link2 className="w-3.5 h-3.5" /> Rattacher à un employé</Label>
            <Select value={form.employe_id || '_none'} onValueChange={v => setForm(f => ({ ...f, employe_id: v === '_none' ? '' : v }))}>
              <SelectTrigger data-testid="user-employe-select"><SelectValue placeholder="Aucun employé" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">— Aucun —</SelectItem>
                {employes.map(emp => (
                  <SelectItem key={emp.id} value={emp.id}>
                    {emp.prenom} {emp.nom || ''} — {emp.poste || ''}
                    {emp.user_id && emp.user_id !== (existingUser?.id || '') ? ' (déjà lié)' : ''}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label>{existingUser ? 'Nouveau mot de passe (laisser vide pour ne pas changer)' : 'Mot de passe'}</Label>
            <Input type="password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))}
              placeholder={existingUser ? 'Laisser vide...' : ''} data-testid="user-password-input" />
          </div>

          {!isAdmin && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <Label className="text-base font-semibold">Droits d'acces aux modules</Label>
                <Button variant="ghost" size="sm" onClick={toggleAll} className="text-xs">
                  {form.permissions.length === modules.length ? 'Tout decocher' : 'Tout cocher'}
                </Button>
              </div>
              <div className="grid grid-cols-2 gap-2 max-h-[250px] overflow-y-auto border rounded-md p-3" data-testid="permissions-grid">
                {modules.map(mod => (
                  <div key={mod.id} className="flex items-center gap-2 p-1.5 rounded hover:bg-muted/50">
                    <Checkbox
                      checked={form.permissions.includes(mod.id)}
                      onCheckedChange={() => togglePermission(mod.id)}
                      data-testid={`perm-${mod.id}`}
                    />
                    <span className="text-sm">{mod.label}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
          {isAdmin && (
            <div className="p-3 bg-muted/50 rounded-md text-sm text-muted-foreground flex items-center gap-2">
              <ShieldCheck className="w-4 h-4" />
              Les administrateurs ont acces a tous les modules automatiquement.
            </div>
          )}

          <Button onClick={handleSubmit} className="w-full" data-testid="save-user-btn">
            <Save className="w-4 h-4 mr-2" />
            {existingUser ? 'Enregistrer' : 'Creer l\'utilisateur'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function UserManagementPage() {
  const { api } = useAuth();
  const [users, setUsers] = useState([]);
  const [modules, setModules] = useState([]);
  const [employes, setEmployes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [usersRes, modsRes, empsRes] = await Promise.all([
        api.get('/admin/users'),
        api.get('/admin/modules'),
        api.get('/admin/employes-list'),
      ]);
      setUsers(usersRes.data);
      setModules(modsRes.data);
      setEmployes(empsRes.data);
    } catch (err) {
      toast.error('Erreur chargement');
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/admin/sync-employes');
      toast.success(res.data.message);
      fetchData();
    } catch (err) {
      toast.error('Erreur synchronisation');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveUser = async (form, existingId) => {
    try {
      if (existingId) {
        const payload = { nom: form.nom, role: form.role, permissions: form.permissions };
        if (form.password) payload.password = form.password;
        if (form.employe_id !== undefined) payload.employe_id = form.employe_id;
        const res = await api.put(`/admin/users/${existingId}`, payload);
        setUsers(prev => prev.map(u => u.id === existingId ? res.data : u));
        toast.success('Utilisateur mis a jour');
      } else {
        const res = await api.post('/admin/users', form);
        setUsers(prev => [...prev, res.data]);
        toast.success('Utilisateur cree');
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await api.delete(`/admin/users/${userId}`);
      setUsers(prev => prev.filter(u => u.id !== userId));
      toast.success('Utilisateur supprime');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const handleResetPassword = async (user) => {
    if (!window.confirm(`Reinitialiser le mot de passe de ${user.nom} ?\nL'utilisateur devra le changer a la prochaine connexion.`)) return;
    try {
      const res = await api.post(`/admin/users/${user.id}/reset-password`);
      toast.success(`Mot de passe reinitialise : "${res.data.temp_password}" — Changement obligatoire a la connexion`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur');
    }
  };

  const getRoleBadge = (role) => {
    const r = ROLES.find(r => r.id === role);
    if (!r) return <Badge variant="secondary">{role}</Badge>;
    if (role === 'admin') return <Badge className="bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"><ShieldCheck className="w-3 h-3 mr-1" />{r.label}</Badge>;
    if (role === 'manager') return <Badge className="bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300"><Shield className="w-3 h-3 mr-1" />{r.label}</Badge>;
    if (role === 'employe') return <Badge className="bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"><UserCheck className="w-3 h-3 mr-1" />{r.label}</Badge>;
    return <Badge className="bg-gray-100 text-gray-800 dark:bg-gray-800 dark:text-gray-300"><Eye className="w-3 h-3 mr-1" />{r.label}</Badge>;
  };

  if (loading) return <div className="h-96 flex items-center justify-center"><p className="text-muted-foreground">Chargement...</p></div>;

  return (
    <div className="space-y-6" data-testid="user-management-page">
      <div className="module-header">
        <div>
          <h1 className="module-title flex items-center gap-3">
            <Users className="w-8 h-8 text-primary" />
            Gestion des utilisateurs
          </h1>
          <p className="text-muted-foreground mt-1">Creer des comptes et gerer les droits d'acces par module</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" className="gap-2" onClick={handleSync} disabled={syncing} data-testid="sync-employes-btn">
            <RefreshCw className={`w-4 h-4 ${syncing ? 'animate-spin' : ''}`} /> Synchro auto
          </Button>
          <UserFormDialog
            modules={modules}
            employes={employes}
            onSave={handleSaveUser}
            trigger={<Button className="gap-2" data-testid="create-user-btn"><Plus className="w-4 h-4" /> Nouvel utilisateur</Button>}
          />
        </div>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nom</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Employé rattaché</TableHead>
                <TableHead>Modules autorises</TableHead>
                <TableHead className="text-center w-[100px]">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map(user => (
                <TableRow key={user.id} data-testid={`user-row-${user.id}`}>
                  <TableCell className="font-medium">{user.nom}</TableCell>
                  <TableCell className="text-muted-foreground">{user.email}</TableCell>
                  <TableCell>{getRoleBadge(user.role)}</TableCell>
                  <TableCell>
                    {user.employe_info ? (
                      <div className="flex items-center gap-2">
                        <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-xs font-bold text-primary">
                          {user.employe_info.prenom?.[0]}
                        </div>
                        <div>
                          <p className="text-sm font-medium leading-none">{user.employe_info.prenom} {user.employe_info.nom || ''}</p>
                          <p className="text-xs text-muted-foreground">{user.employe_info.poste}</p>
                        </div>
                      </div>
                    ) : (
                      <span className="text-xs text-muted-foreground italic">Non rattaché</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.role === 'admin' ? (
                      <span className="text-sm text-muted-foreground italic">Tous les modules</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {(user.permissions || []).length === 0 ? (
                          <span className="text-sm text-muted-foreground italic">Aucun module</span>
                        ) : (
                          (user.permissions || []).slice(0, 5).map(p => {
                            const mod = modules.find(m => m.id === p);
                            return <Badge key={p} variant="outline" className="text-xs">{mod?.label || p}</Badge>;
                          })
                        )}
                        {(user.permissions || []).length > 5 && (
                          <Badge variant="secondary" className="text-xs">+{user.permissions.length - 5}</Badge>
                        )}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex justify-center gap-1">
                      <UserFormDialog
                        modules={modules}
                        employes={employes}
                        existingUser={user}
                        onSave={handleSaveUser}
                        trigger={
                          <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`edit-user-${user.id}`} title="Modifier">
                            <Pencil className="w-4 h-4" />
                          </Button>
                        }
                      />
                      {user.role !== 'admin' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-amber-600" onClick={() => handleResetPassword(user)} data-testid={`reset-pwd-${user.id}`} title="Reinitialiser mot de passe">
                          <KeyRound className="w-4 h-4" />
                        </Button>
                      )}
                      {user.role !== 'admin' && (
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => handleDelete(user.id)} data-testid={`delete-user-${user.id}`} title="Supprimer">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}
